import { RotateCcw, Save, Wand2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/empty-state';
import { FormError } from '@/components/ui/field';
import { ApiError } from '@/lib/api';
import { formatDuration } from '@/lib/dates';
import { useToast } from '@/lib/toast-context';
import { cn } from '@/lib/utils';
import { isInvalid, minutesOf, type WeeklyDraft } from '@/pages/admin/scheduling/useWeeklyDraft';
import { SCHEDULE_PRESETS } from '@shared/schemas/availability';

/*
 * The weekly template — ROADMAP.md §7.2, FINDINGS.md §2 ("the strongest single
 * page. Rebuild this close to as-is").
 *
 * One toggle per day, from and to, break minutes, **a computed hours badge per
 * row**, and the five quick presets underneath. The running weekly total lives
 * in the pane's header above this, because it is a fact about the person rather
 * than about this form — and both read it from `useWeeklyDraft`, so they cannot
 * disagree.
 *
 * The form holds all seven days and saves all seven at once. Saving a day at a
 * time would make "Monday off" and "Monday not filled in yet" the same request,
 * and the resolver reads both as unavailable — for opposite reasons.
 */

export function WeeklyScheduleEditor({
  draft,
  employeeName,
}: {
  draft: WeeklyDraft;
  employeeName: string;
}) {
  const toast = useToast();
  const [formError, setFormError] = useState<string | null>(null);

  if (draft.query.isError) {
    return (
      <ErrorState
        title="That schedule could not be loaded"
        onRetry={() => {
          void draft.query.refetch();
        }}
      />
    );
  }

  if (draft.query.isPending) {
    return (
      <div aria-busy="true" className="space-y-2 p-4">
        <span className="sr-only">Loading the weekly schedule</span>
        {[0, 1, 2, 3, 4, 5, 6].map((row) => (
          <div
            key={row}
            aria-hidden="true"
            className="bg-paper-100 h-14 animate-pulse rounded-sm"
          />
        ))}
      </div>
    );
  }

  function onSubmit(): void {
    setFormError(null);
    draft.submit({
      onSuccess: () => {
        toast.success(`${employeeName}'s weekly schedule saved.`);
      },
      onError: (error) => {
        setFormError(
          error instanceof ApiError ? error.message : 'That schedule could not be saved.',
        );
      },
    });
  }

  return (
    <div className="p-4">
      <ul className="space-y-2">
        {draft.days.map((day) => {
          const invalid = isInvalid(day);

          return (
            <li
              key={day.dayOfWeek}
              className={cn(
                'rounded-sm border p-3',
                day.isWorking ? 'border-honey-500 bg-honey-50' : 'border-ink-300 bg-white',
              )}
            >
              {/*
                `gap-x-3` rather than `gap-x-4`, and the break input is 4rem
                rather than 5. Both are the difference between the hours badge
                sitting on the row and being pushed onto a second line at 1440 —
                the badge belongs beside the hours it describes, which is how
                the prototype reads.
              */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-3">
                {/*
                  The toggle is a real checkbox inside a 44px label. The visual
                  control can be small; the hit area cannot (P23, and the 375px
                  sweep measures rendered boxes rather than reading classes).
                */}
                {/*
                  `7.5rem` fits "Wednesday" and its checkbox with room to
                  spare, and not a pixel more: at 9.5rem the row was wide enough
                  to push the hours badge onto a second line on a 1440 screen,
                  and the badge belongs *on* the row — that is what makes it
                  readable as this day's figure rather than a stray total.
                */}
                <label className="flex min-h-touch min-w-[7.5rem] cursor-pointer items-center gap-2.5">
                  <input
                    type="checkbox"
                    name={`working-${String(day.dayOfWeek)}`}
                    checked={day.isWorking}
                    onChange={(event) => {
                      draft.update(day.dayOfWeek, { isWorking: event.target.checked });
                    }}
                    className="size-4 shrink-0 accent-honey-500"
                  />
                  <span className="text-label text-ink-900">{day.dayLabel}</span>
                </label>

                {day.isWorking ? (
                  <>
                    <div className="flex items-center gap-2">
                      <label
                        htmlFor={`from-${String(day.dayOfWeek)}`}
                        className="text-meta text-ink-500"
                      >
                        From
                      </label>
                      <input
                        id={`from-${String(day.dayOfWeek)}`}
                        name={`from-${String(day.dayOfWeek)}`}
                        type="time"
                        value={day.startTime}
                        onChange={(event) => {
                          draft.update(day.dayOfWeek, { startTime: event.target.value });
                        }}
                        className="min-h-touch rounded-sm border border-ink-300 bg-white px-2 text-body"
                      />
                      <label
                        htmlFor={`to-${String(day.dayOfWeek)}`}
                        className="text-meta text-ink-500"
                      >
                        to
                      </label>
                      <input
                        id={`to-${String(day.dayOfWeek)}`}
                        name={`to-${String(day.dayOfWeek)}`}
                        type="time"
                        value={day.endTime}
                        aria-invalid={invalid || undefined}
                        onChange={(event) => {
                          draft.update(day.dayOfWeek, { endTime: event.target.value });
                        }}
                        className={cn(
                          'min-h-touch rounded-sm border bg-white px-2 text-body',
                          invalid ? 'border-status-priority-text' : 'border-ink-300',
                        )}
                      />
                    </div>

                    {/*
                      **`break_minutes` is here because the prototype has it and
                      the archived roadmap dropped it** (FINDINGS.md §4). It is
                      unpaid and comes out of the hours before overtime is
                      applied (business-rules.md §5), which is why the badge
                      beside it shows the *net* figure rather than the span.
                    */}
                    <div className="flex items-center gap-2">
                      <label
                        htmlFor={`break-${String(day.dayOfWeek)}`}
                        className="text-meta text-ink-500"
                      >
                        Break
                      </label>
                      <input
                        id={`break-${String(day.dayOfWeek)}`}
                        name={`break-${String(day.dayOfWeek)}`}
                        type="number"
                        min={0}
                        max={720}
                        step={5}
                        value={day.breakMinutes}
                        onChange={(event) => {
                          draft.update(day.dayOfWeek, {
                            breakMinutes: Number(event.target.value),
                          });
                        }}
                        className="min-h-touch w-16 rounded-sm border border-ink-300 bg-white px-2 text-body"
                      />
                      <span className="text-meta text-ink-500">min</span>
                    </div>

                    <span
                      className={cn(
                        'ml-auto rounded-full px-2.5 py-1 text-meta font-medium',
                        invalid
                          ? 'bg-red-50 text-status-priority-text'
                          : 'bg-white text-ink-900 ring-1 ring-ink-300',
                      )}
                    >
                      {invalid ? 'Finish is before the start' : formatDuration(minutesOf(day))}
                    </span>
                  </>
                ) : (
                  <span className="ml-auto text-meta text-ink-500">Day off</span>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {/*
        The five quick presets, by their prototype names. They are the reason the
        table gets filled in at all: seven days per cleaner by hand is the
        friction that leaves it empty, and an empty table resolves as
        *unavailable* for everybody (business-rules.md §4).
      */}
      <div className="bg-paper-50 mt-5 rounded-sm border border-ink-300 p-3">
        <p className="flex items-center gap-1.5 text-label text-ink-900">
          <Wand2 aria-hidden="true" className="size-4 text-bark-700" />
          Quick presets
        </p>
        <p className="mt-1 text-meta text-ink-500">
          Sets the whole week, including the days it leaves off. Nothing is saved until you press
          Save.
        </p>
        <div className="mt-2.5 flex flex-wrap gap-2">
          {SCHEDULE_PRESETS.map((preset) => (
            <Button
              key={preset.id}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                draft.applyPreset(preset.id);
                setFormError(null);
              }}
            >
              {preset.label}
            </Button>
          ))}
        </div>
      </div>

      {formError ? (
        <div className="mt-4">
          <FormError>{formError}</FormError>
        </div>
      ) : null}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <Button
          type="button"
          onClick={onSubmit}
          disabled={draft.save.isPending || !draft.dirty || draft.hasInvalidDay}
        >
          <Save aria-hidden="true" />
          {draft.save.isPending ? 'Saving…' : 'Save weekly schedule'}
        </Button>

        {draft.dirty ? (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              draft.discard();
              setFormError(null);
            }}
          >
            <RotateCcw aria-hidden="true" />
            Discard changes
          </Button>
        ) : null}

        <p aria-live="polite" className="text-meta text-ink-500">
          {draft.hasInvalidDay
            ? 'Fix the day marked in red first'
            : draft.dirty
              ? 'Unsaved changes'
              : 'Saved'}
        </p>
      </div>
    </div>
  );
}
